import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { fetchPowerBIEmbedToken } from '../services/powerbiService';

import { useUser } from '../context/UserContext';

export default function PowerBIEmbedComponent({ reportId, datasetId, workspaceId, height = 700, width = '100%', pageName, useRLS = true, roleName = 'RLS_Country', showPageNavigator = false, showFilterPane = false, countryFilterTarget }) {
  const [embedInfo, setEmbedInfo] = useState(null);
  const [error, setError] = useState(null);
  const [reportError, setReportError] = useState(null);
  const reportRef = useRef(null);
  const containerRef = useRef(null);
  const [isSized, setIsSized] = useState(false);
  const [reportLoaded, setReportLoaded] = useState(false);
  const { user } = useUser();

  const allowedCountriesKey = React.useMemo(
    () => (Array.isArray(user?.allowedCountriesIso3) ? user.allowedCountriesIso3.join(',') : ''),
    [user?.allowedCountriesIso3]
  );
  const countries = React.useMemo(
    () => (allowedCountriesKey ? allowedCountriesKey.split(',') : undefined),
    [allowedCountriesKey]
  );
  const resolvedPageName = pageName || embedInfo?.pageName;

  useEffect(() => {
    let isMounted = true;
    setError(null);
    setReportError(null);
    setEmbedInfo(null);
    setReportLoaded(false);
    (async () => {
      try {
        const username = user?.email || '';
        const data = await fetchPowerBIEmbedToken(reportId, workspaceId, username, countries, useRLS, roleName, datasetId);
        if (!isMounted) return;
        setEmbedInfo(data);
      } catch (e) {
        if (!isMounted) return;
        setError(e.message || 'Failed to load Power BI report');
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [reportId, datasetId, workspaceId, user?.email, countries, useRLS, roleName]);

  useEffect(() => {
    if (!reportLoaded || !reportRef.current || !resolvedPageName) return undefined;
    let cancelled = false;
    setReportError(null);

    (async () => {
      try {
        const report = reportRef.current;
        const pages = await report.getPages();
        if (cancelled) return;
        const target = pages.find(p => p.name === resolvedPageName || p.displayName === resolvedPageName);
        if (!target) throw new Error(`Power BI page not found: ${resolvedPageName}`);
        if (countryFilterTarget) {
          const isCountryFilter = (filter) => filter.target?.table === countryFilterTarget.table
            && filter.target?.column === countryFilterTarget.column;
          const reportFilters = await report.getFilters();
          if (cancelled) return;
          if (!reportFilters.some(isCountryFilter)) {
            await report.updateFilters(models.FiltersOperations.Add, [{
              $schema: 'http://powerbi.com/product/schema#basic',
              target: countryFilterTarget,
              filterType: models.FilterType.BasicFilter,
              operator: 'All', values: [],
              displaySettings: { displayName: 'Country' }
            }]);
          }
          const pageFilters = await target.getFilters();
          if (cancelled) return;
          // Menu links open detail pages directly, without drillthrough context.
          // Replace only the saved country context with the editable report filter;
          // keep antigen, date and all other page filters, plus server-enforced RLS.
          if (pageFilters.some(isCountryFilter)) {
            await target.setFilters(pageFilters.filter((filter) => !isCountryFilter(filter)));
          }
        }
        if (cancelled) return;
        await target.setActive();
      } catch (e) {
        if (!cancelled) setReportError(e.message || 'Could not open the requested Power BI page');
      }
    })();
    return () => { cancelled = true; };
  }, [reportLoaded, resolvedPageName, countryFilterTarget]);

  // Wait until container has a real size before embedding to avoid tiny iframe (w=2)
  useLayoutEffect(() => {
    let rafId;
    const check = () => {
      const el = containerRef.current;
      if (el && el.clientWidth > 100 && el.clientHeight > 100) {
        setIsSized(true);
      } else {
        rafId = requestAnimationFrame(check);
      }
    };
    check();
    return () => cancelAnimationFrame(rafId);
  }, [width, height]);

  // Keep responsive on container resizes
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => {
      try {
        reportRef.current?.resize?.();
      } catch (e) {
        // noop
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (error) return <div className="text-red-600">{error}</div>;
  if (!embedInfo) return <div className="text-gray-600">Loading report…</div>;

  return (
    <div className="w-full h-full">
    {reportError && <div role="alert" className="text-red-600">{reportError}</div>}
    <div ref={containerRef} className="w-full h-full" style={{
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
      //minHeight: typeof height === 'number' ? `${Math.min(600, height)}px` : '600px'
    }}>
      {isSized && (
        <PowerBIEmbed
      embedConfig={{
        type: 'report',
        id: embedInfo.reportId,
        embedUrl: embedInfo.embedUrl,
        accessToken: embedInfo.accessToken,
        tokenType: models.TokenType.Embed,
        // pageName in embedConfig accepts an internal ID, not a display name.
        // Resolve the exact requested name through getPages() after loading.
        settings: {
          panes: {
            filters: { visible: showFilterPane, expanded: showFilterPane },
            pageNavigation: { visible: showPageNavigator }
          },
          navContentPaneEnabled: showPageNavigator,
          layoutType: models.LayoutType.Custom,
          //background: models.BackgroundType.Transparent,
          customLayout: { displayOption: models.DisplayOption.FitToPage }
        }
      }}
      cssClassName="w-full h-full"
      eventHandlers={
        new Map([
          [
            'loaded',
            async () => {
              try {
                if (!reportRef.current) return;
                await reportRef.current.updateSettings({
                  panes: {
                    filters: { visible: showFilterPane, expanded: showFilterPane },
                    pageNavigation: { visible: showPageNavigator }
                  },
                  navContentPaneEnabled: showPageNavigator,
                  layoutType: models.LayoutType.Custom,
                  //background: models.BackgroundType.Transparent,
                  customLayout: { displayOption: models.DisplayOption.FitToPage }
                });

                setReportLoaded(true);
              } catch (e) {
                setError(e.message || 'Could not initialize the Power BI report');
              }
            }
          ],
          [
            'error',
            (event) => {
              const detail = event?.detail || event;
              // Power BI displays visual failures within the affected visual.
              // Keep their diagnostics without duplicating them above the report.
              if (/could not render a report visual/i.test(detail?.detailedMessage || '')) {
                console.warn('PBI visual error detail:', detail);
                return;
              }
              console.error('PBI error detail:', detail);
              const msg = detail?.message || detail || 'Power BI error occurred';
              setReportError(typeof msg === 'string' ? msg : JSON.stringify(msg));
            }
          ],
          [
            'rendered',
            async () => {
              try {
                if (!reportRef.current) return;
                await reportRef.current.updateSettings({
                  layoutType: models.LayoutType.Custom,
                  customLayout: { displayOption: models.DisplayOption.FitToPage }
                });
                reportRef.current.resize?.();
              } catch (e) {
                // ignore
              }
            }
          ]
        ])
      }
      getEmbeddedComponent={(embeddedReport) => {
        reportRef.current = embeddedReport;
      }}
        />
      )}
    </div>
    </div>
  );
}


